import type {MouseEvent, ReactElement, ReactNode} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import type {TicketingQuestionBoxProps} from './ticketing-question-box.types';
import styles from './ticketing-question-box.module.scss';

const BOX_CLASS = 'gap-2 bg-neutral-alt-primary-default flex-row-align-center align-items-start spacing-p-3 corner-radius-8 _full-width _position-relative';

const stopPropagation = (event: MouseEvent): void => {
  event.stopPropagation();
};

export function TicketingQuestionBox({displayNumber, pills, dragHandleClass, actions, onCardClick, children}: TicketingQuestionBoxProps): ReactElement {
  const getDragHandle = (): ReactElement | null => {
    if (dragHandleClass === undefined) {
      return null;
    }
    return (
      <Icon
        className={`${styles.dragHandle} ${dragHandleClass} _position-absolute ct-neutral-tertiary`}
        icon="icon-draggable"
        size={IconSize.SIZE_ICON_16}
        onClick={stopPropagation}
      />
    );
  };

  const getActions = (): ReactElement | null => {
    if (actions === undefined) {
      return null;
    }
    return (
      <div className="gap-3 flex-row-align-center">
        <Icon
          className={styles.actionIcon}
          icon="icon-delete"
          size={IconSize.SIZE_ICON_16}
          onClick={(event): void => {
            event.stopPropagation();
            actions.onDelete();
          }}
          ariaLabel={window.i18next.t('pmwjs_delete')}
          isUsedAsButton
        />
        <Icon
          className={styles.actionIcon}
          icon="icon-pencil"
          size={IconSize.SIZE_ICON_16}
          onClick={(event): void => {
            event.stopPropagation();
            actions.onEdit();
          }}
          ariaLabel={window.i18next.t('pmwjs_edit')}
          isUsedAsButton
        />
      </div>
    );
  };

  const getInner = (): ReactNode => {
    return (
      <>
        {getDragHandle()}
        <div className="gap-3 flex-column-align-start flex-1">
          <div className="flex-row-justify-between _full-width">
            <Text className="ct-neutral-tertiary" val={window.i18next.t('pmwjs_event_ticketing_question_number', {n: displayNumber})} size={TextSize.SM} />
            {getActions()}
          </div>
          <div className={`${styles.pillRow} flex-row-align-center`}>{pills}</div>
          {children}
        </div>
      </>
    );
  };

  if (onCardClick === undefined) {
    return <div className={BOX_CLASS}>{getInner()}</div>;
  }

  return (
    <ClickableDiv className={`${BOX_CLASS} ${styles.clickableCard}`} onClick={onCardClick} aria-label={window.i18next.t('pmwjs_edit')}>
      {getInner()}
    </ClickableDiv>
  );
}
